import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { returnSubmit } from "../enums/queryFunction/Submit";
import { SnackbarContext } from "../contexts/Snackbar";
import { HttpStatusCode } from "axios";
import { DOC_CATEGORY } from "../enums/QueryKeys";
import { invalidate_queries } from "../features/master/species/hooks/useSubmit";

function getErrorMessage(error) {
  if (typeof error === "string" && error.trim() !== "") {
    return error;
  }
  if (error && typeof error === "object" && typeof error.message === "string") {
    return error.message;
  }
  return "errorMessage";
}

export default function usePut(url, key, invalidateQueries = []) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const queryClient = useQueryClient();

  const queryKey = key;

  const submitCallback = async (obj) => {
    setLoading(true);
    try {
      const response = await mutateAsync(obj);
      if (response?.status) {
        if (response.status == HttpStatusCode.Ok) {
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  function updateKey(key, newData) {
    if (invalidate_queries.some((q) => String(key).startsWith(q))) {
      queryClient.invalidateQueries({ queryKey: [key] });
      return;
    }
    queryClient.setQueryData(Array.isArray(key) ? key : [key], (oldData) => {
      if (!oldData) return oldData;
      if (!Array.isArray(oldData) && typeof oldData === "object") {
        const updatedEntity = newData;
        const result = { ...oldData };

        for (const field of Object.keys(result)) {
          if (Array.isArray(result[field])) {
            const index = result[field].findIndex(
              (entity) => Number(entity.id) === Number(updatedEntity.id),
            );
            if (index >= 0) {
              const newArr = [...result[field]];
              newArr[index] = updatedEntity;
              result[field] = newArr;
            }
          } else if (
            result[field] &&
            typeof result[field] === "object" &&
            Number(result[field].id) === Number(updatedEntity.id)
          ) {
            result[field] = updatedEntity;
          }
        }

        return result;
      }
      if (Array.isArray(oldData)) {
        const index = oldData.findIndex(
          (entity) => Number(entity.id) === Number(newData.id),
        );
        if (index >= 0) {
          const newArr = [...oldData];
          newArr[index] = newData;
          return newArr;
        }
        return [...oldData, newData];
      }
      return oldData;
    });
  }

  const { mutateAsync } = useMutation({
    mutationFn: (data) => returnSubmit(url, data),
    onSuccess: (response) => {
      if (key != null) {
        if (response?.data) {
          if (Array.isArray(queryKey)) {
            queryKey.forEach((key) => updateKey(key, response?.data));
          } else {
            updateKey(queryKey, response?.data);
          }
        }

        const queriesToInvalidate = Array.isArray(invalidateQueries)
          ? invalidateQueries
          : invalidateQueries
            ? [invalidateQueries]
            : [];
        queriesToInvalidate.forEach((query) =>
          queryClient.invalidateQueries({
            queryKey: Array.isArray(query) ? query : [query],
          }),
        );

        setResult(true);
        handleSnackbar("successMessage", "success");
        setLoading(false);
      }
    },
    onError: (error) => {
      handleSnackbar(getErrorMessage(error), "error");
      setLoading(false);
    },
  });

  return {
    submitCallback,
    loading,
    result,
  };
}

export function usePutNoFeedback(url, key, invalidateQueries = []) {
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const queryClient = useQueryClient();

  const queryKey = key;

  const submitCallback = async (obj) => {
    setLoading(true);
    try {
      const response = await mutateAsync(obj);
      if (response?.status) {
        if (response.status == HttpStatusCode.Ok) {
          if (response?.data) {
            return response?.data;
          } else {
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      return error;
    }
  };

  function updateKey(key, newData) {
    if (invalidate_queries.includes(String(key))) {
      queryClient.invalidateQueries({ queryKey: [DOC_CATEGORY] });
    } else {
      const updatedEntity = newData;
      queryClient.setQueryData(Array.isArray(key) ? key : [key], (oldData) => {
        if (!oldData) return oldData;
        if (Array.isArray(oldData)) {
          return oldData.map((entity) =>
            Number(entity.id) === Number(updatedEntity.id)
              ? updatedEntity
              : entity
          );
        } else {
          return updatedEntity;
        }
      });
    }
  }

  const { mutateAsync } = useMutation({
    mutationFn: (data) => returnSubmit(url, data),
    onSuccess: (response) => {
      if (key != null) {
        if (response?.data) {
          if (Array.isArray(queryKey)) {
            queryKey.forEach((key) => updateKey(key, response?.data));
          } else {
            updateKey(queryKey, response?.data);
          }
        }

        const queriesToInvalidate = Array.isArray(invalidateQueries)
          ? invalidateQueries
          : invalidateQueries ? [invalidateQueries] : [];
        queriesToInvalidate.forEach((query) =>
          queryClient.invalidateQueries({ queryKey: Array.isArray(query) ? query : [query] })
        );

        setResult(true);
        setLoading(false);
      }
    },
    onError: (error) => {
      handleSnackbar(getErrorMessage(error), "error");
      setLoading(false);
    },
  });

  return {
    submitCallback,
    loading,
    result,
  };
}